import type { Book, Ref } from './types';

/**
 * URL shape: `/?c=esv.john.3,kjv.john.3`
 *
 * Each segment is `translation.book-slug.chapter[.verse]`. Book slugs use
 * hyphens, so periods stay unambiguous as separators. The query string is
 * assembled by hand rather than with URLSearchParams, which would percent-encode
 * the separators and make the URL unreadable.
 */

export const sameChapter = (a: Ref, b: Ref) =>
  a.book === b.book && a.chapter === b.chapter;

export function encodeRefs(refs: Ref[], books: Book[]): string {
  const slug = (id: number) => books.find((b) => b.id === id)?.slug ?? String(id);
  return refs
    .map((r) =>
      [r.translation, slug(r.book), r.chapter, r.verse].filter(Boolean).join('.')
    )
    .join(',');
}

export function decodeRefs(
  value: string | null,
  books: Book[],
  translations: { id: string }[],
): Ref[] {
  if (!value) return [];
  const refs: Ref[] = [];

  for (const segment of value.split(',')) {
    const parts = segment.trim().toLowerCase().split('.');
    if (parts.length < 3) continue;

    const [translationId, bookSlug, chapterStr, verseStr] = parts;
    if (!translations.some((t) => t.id === translationId)) continue;

    const book = books.find((b) => b.slug === bookSlug);
    if (!book) continue;

    const chapter = Number(chapterStr);
    if (!Number.isInteger(chapter) || chapter < 1 || chapter > book.chapters) continue;

    const verse = Number(verseStr);
    refs.push({
      translation: translationId,
      book: book.id,
      chapter,
      verse: Number.isInteger(verse) && verse > 0 ? verse : undefined,
    });
  }
  return refs;
}

/** Flat list of every [bookId, chapter] pair, for uniform random selection. */
function allChapters(books: Book[]): [number, number][] {
  const out: [number, number][] = [];
  for (const book of books) {
    for (let c = 1; c <= book.chapters; c++) out.push([book.id, c]);
  }
  return out;
}

let chapterIndex: [number, number][] | null = null;

export function randomChapter(books: Book[]): { book: number; chapter: number } {
  chapterIndex ??= allChapters(books);
  const [book, chapter] = chapterIndex[Math.floor(Math.random() * chapterIndex.length)];
  return { book, chapter };
}

/** Steps by one chapter, rolling over book boundaries and wrapping the canon. */
export function step(ref: Ref, books: Book[], delta: 1 | -1): Ref {
  const index = books.findIndex((b) => b.id === ref.book);
  if (index === -1) return ref;

  let bookIndex = index;
  let chapter = ref.chapter + delta;

  if (chapter > books[bookIndex].chapters) {
    bookIndex = (bookIndex + 1) % books.length;
    chapter = 1;
  } else if (chapter < 1) {
    bookIndex = (bookIndex - 1 + books.length) % books.length;
    chapter = books[bookIndex].chapters;
  }

  return { translation: ref.translation, book: books[bookIndex].id, chapter };
}
